import React, { useMemo } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import moment from "moment-timezone";
import { Button, Container, Icon, Label } from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import { Assign, OpenAPI } from "simplydo/interfaces";
import { AccessibleDropdown } from "simplydo/react/components/Accessibility/AccessibleDropdown";

import { useAppSelector } from "store";
import util from "utils/utils";

import { AnonymousChip, ProfileChip } from "components/lib/Chips";
import Tag, { TagContainer } from "components/lib/Tag";
import { Banner, PlaceholderItem, PlaceholderStack } from "components/lib/UI";

const StyledCoverImage = styled.div<{ $image: string }>`
  border-radius: 3px;
  min-width: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  max-height: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  max-width: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  min-height: ${({ theme }) => (theme.sizes.isMobile ? "100px" : "200px")};
  border: 3px solid white;
  margin-right: 15px;
  box-shadow: 0px 10px 10px rgba(0, 0, 0, 0.3);
  background-size: cover;
  background-position: center center;
  background-image: url(${({ $image }) => $image});
`;

export const StyledContainer = styled(Container)`
  ${({ theme, fluid }) =>
    theme.sizes.isComputer ?
      `
    padding: 60px 0px 30px;
    ${
      fluid &&
      `
      padding: 1rem 3rem;
    `
    }
  `
    : `
    padding: 10px 0;
  `}
`;

// Also used in group header
export const HeaderContainer = styled.div<{ $isPreview?: boolean }>`
  display: flex;
  width: 100%;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  justify-content: space-between;
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "flex-start" : "flex-end"};
  ${({ $isPreview }) =>
    $isPreview &&
    `
    padding: 0 2rem;
  `}
  margin-top: 3px;
`;

export const HeaderInfo = styled.div<{ $user?: OpenAPI.Schemas["User"] }>`
  display: flex;
  flex-direction: ${({ theme }) => (theme.sizes.isMobile ? "column" : "row")};
  align-items: ${({ theme }) =>
    theme.sizes.isMobile ? "center" : "flex-start"};
  ${({ theme }) =>
    theme.sizes.isMobile &&
    `
    margin-top: 5px;
  `}
  width: 100%;
  h1,
  h5,
  span:not(.dropdown .text) {
    margin: 0;
    color: white;
  }
  .titles {
    margin-bottom: 1rem;
    .meta {
      display: block;
      font-size: 1.1em;
      margin-bottom: 2.5px;
    }
  }
  .owner {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: ${({ $user }) => $user && "1rem"};
    color: white;
  }
  .info {
    display: flex;
    flex-direction: column;
    width: 100%;
  }
  .sector {
    text-transform: uppercase;
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  min-width: 200px;
  flex-direction: row;
  justify-content: flex-end;
  .ui.button {
    &:not(:last-of-type) {
      margin-right: 0.5rem;
    }
  }
  ${({ theme }) =>
    theme.sizes.isMobile &&
    `
    margin-bottom: 1rem;
  `}
`;

type ChallengeHeaderProps = {
  challenge: Assign<
    OpenAPI.Schemas["Challenge"],
    OpenAPI.Schemas["AssignChallengeOrgOwner"],
    OpenAPI.Schemas["AssignTag"],
    OpenAPI.Schemas["AssignChallengeUrls"],
    OpenAPI.Schemas["AssignChallengeOwner"]
  >;
  myIdeas: OpenAPI.Schemas["Idea"][];
  newIdea: () => void;
  isPreview: boolean;
  isIdeasPage: boolean;
  redirectToLogin: () => void;
  loading: boolean;
};

const ChallengeHeader = ({
  challenge,
  myIdeas,
  newIdea,
  isPreview,
  isIdeasPage,
  redirectToLogin,
  loading,
}: ChallengeHeaderProps) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const canMakeIdea = util.canCreateIdea(user, challenge);

  const myUnsubmittedIdeas = useMemo(
    () => myIdeas?.filter((idea) => !idea.isSubmitted),
    [myIdeas],
  );
  return (
    <Banner
      marginless
      image={util.getThumbnail(challenge, "bannerImage", "2560x400")}
    >
      <StyledContainer fluid={isIdeasPage}>
        {loading ?
          <PlaceholderStack gap={5}>
            <PlaceholderItem width="100%" height={50} />
            <PlaceholderItem width="100%" height={30} />
            <PlaceholderItem width="100%" height={20} />
          </PlaceholderStack>
        : <>
            <div className="overlay" />
            <div className="overlay-body">
              <TagContainer>
                {challenge.ownerTags?.map((tag) => (
                  <Tag key={tag._id} tag={tag} />
                ))}
              </TagContainer>
              <HeaderContainer $isPreview={isPreview}>
                <HeaderInfo $user={user}>
                  {challenge.coverImageUrl && (
                    <StyledCoverImage
                      $image={util.getThumbnail(
                        challenge,
                        "coverImage",
                        "500x500",
                      )}
                    />
                  )}
                  <div className="info">
                    <div className="titles">
                      <h1
                        style={{ marginBottom: 3 }}
                        className="challenge-tour-step-1"
                      >
                        {challenge?.name}
                      </h1>
                      {challenge.tagline ?
                        <span style={{ marginBottom: 5 }} className="meta">
                          {challenge.tagline}
                        </span>
                      : null}
                      {challenge.ownerOrganisation ?
                        <h5>
                          {" "}
                          {t("challenge.managedBy")}{" "}
                          {challenge.ownerOrganisation.name}
                        </h5>
                      : null}
                    </div>
                    <div>
                      <div className="owner">
                        <span className="posted-by" style={{ marginRight: 5 }}>
                          {t("challenge.postedBy")}
                        </span>
                        {challenge.postedByType === "anonymous" ?
                          <AnonymousChip />
                        : null}
                        {(
                          challenge.postedByType === "organisation" ||
                          challenge.postedByType === "profile"
                        ) ?
                          <ProfileChip
                            profile={challenge.postedBy}
                            inverted
                            forType={challenge.postedByType}
                          />
                        : null}
                      </div>
                      {!user && (
                        <div style={{ marginBottom: "2rem" }}>
                          {!challenge.stage && (
                            <Label color="yellow" style={{ marginRight: 10 }}>
                              {t("challenge.stages.draft.title")}
                            </Label>
                          )}
                          {challenge.stage === "published" && (
                            <Label color="green" style={{ marginRight: 10 }}>
                              {t("challenge.stages.published.title")}
                            </Label>
                          )}
                          {challenge.stage === "closed" && (
                            <Label color="red" style={{ marginRight: 10 }}>
                              {t("generic.closed")}
                            </Label>
                          )}
                        </div>
                      )}
                      {challenge.deadline ?
                        <span>
                          <Icon name="calendar" />
                          {t("challenge.deadline")}:{" "}
                          {moment(challenge.deadline).format(
                            "HH:mm on DD/MM/YYYY",
                          )}
                        </span>
                      : null}
                    </div>
                    <HeaderActions>
                      {(
                        (canMakeIdea || !user) &&
                        (challenge.stage === "published" ||
                          util.canManageChallenge(user, challenge) ||
                          util.hasPermission(
                            user,
                            "challenge.editSettings",
                            challenge?._id,
                          ))
                      ) ?
                        <Button
                          size={theme.sizes.isMobile ? "mini" : undefined}
                          icon="plus"
                          content={
                            challenge?.buttonText ?
                              challenge.buttonText
                            : t("challenge.ideas.createYourIdea")
                          }
                          primary={myUnsubmittedIdeas?.length === 0}
                          inverted={myUnsubmittedIdeas?.length > 0}
                          onClick={
                            canMakeIdea ? newIdea : () => redirectToLogin()
                          }
                        />
                      : null}
                      {user && myUnsubmittedIdeas?.length > 0 && (
                        <React.Fragment>
                          {myUnsubmittedIdeas?.length === 1 && (
                            <Button
                              size={theme.sizes.isMobile ? "mini" : undefined}
                              icon="lightbulb"
                              content={t("challenge.continueIdea")}
                              primary
                              as={Link}
                              to={`/ideas/${myUnsubmittedIdeas[0]._id}`}
                            />
                          )}
                          {myUnsubmittedIdeas?.length > 1 && (
                            <AccessibleDropdown
                              icon={null}
                              direction="left"
                              pointing="top right"
                              trigger={
                                <Button
                                  size={
                                    theme.sizes.isMobile ? "mini" : undefined
                                  }
                                  icon="lightbulb"
                                  content={t("challenge.continueIdea")}
                                  primary
                                />
                              }
                              options={myUnsubmittedIdeas.map((i) => ({
                                key: i._id,
                                as: "a",
                                href: `/ideas/${i._id}`,
                                text: i.name,
                                value: i._id,
                              }))}
                            />
                          )}
                        </React.Fragment>
                      )}
                    </HeaderActions>
                  </div>
                </HeaderInfo>
              </HeaderContainer>
            </div>
          </>
        }
      </StyledContainer>
    </Banner>
  );
};

export default ChallengeHeader;
